import { pdkAxios } from '@/lib/pdk-axios';
import { VehicleFromIndexer, VehicleListing } from '@/models/registry';
import type {
  VEHICLE_LISTINGS_GROUPED_BY_PURCHASE_STATUS,
  VehicleFromIndexer as VehicleFromIndexerType,
  VehicleListingPurchaseStatus,
  VehicleListing as VehicleListingType,
} from '@/models/types';
import dayjs from 'dayjs';
import { makeAutoObservable } from 'mobx';
import { db } from '../db';
import type { DeltaPacketRecord } from '../hydrate';
import { hydrateFromWorker } from '../hydrate-worker-utils';
import type { BaseStore } from './base';
import { dealStore } from './deals';

export class VehicleListingStore
  implements BaseStore<VehicleListing, VehicleListingType>
{
  store: Map<string, VehicleListing> = new Map();

  // deal id --> vehicle listing
  storeIndexedByForeignKey: Map<string, VehicleListing> = new Map();

  // vehicle listing id --> deal id
  storeReferenceToDealByVehicleListingId: Map<string, string> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  applyDelta({ delta }: { delta: DeltaPacketRecord<VehicleListingType> }) {
    const vehicleListing = this.getById(delta.record_id);
    if (!vehicleListing) return;

    // update all fields
    vehicleListing.updated_at = delta.data.updated_at;
    vehicleListing.status = delta.data.status;
    vehicleListing.purchase_status = delta.data.purchase_status;
    vehicleListing.price = delta.data.price;
    vehicleListing.mileage = delta.data.mileage;
    vehicleListing.num_of_keys = delta.data.num_of_keys;
    vehicleListing.vehicle_history = delta.data.vehicle_history;
    vehicleListing.previous_owners = delta.data.previous_owners;
    vehicleListing.is_pre_order = delta.data.is_pre_order;
    vehicleListing.transaction_type = delta.data.transaction_type;
    vehicleListing.is_listed_wholesale = delta.data.is_listed_wholesale;
    vehicleListing.remaining_battery_warranty_miles =
      delta.data.remaining_battery_warranty_miles;
    vehicleListing.remaining_battery_warranty_months =
      delta.data.remaining_battery_warranty_months;
    vehicleListing.updated_at = delta.data.updated_at;

    this.save(vehicleListing);

    // hydrate indexed db
    db.vehicle_listings.put(vehicleListing, vehicleListing.id);
  }

  add(input: VehicleListingType) {
    const vehicleListing = new VehicleListing(input);
    this.store.set(vehicleListing.id, vehicleListing);
  }

  postSwapReindexing({
    previousVehicleListingId,
    newVehicleListingId,
    dealId,
  }: {
    previousVehicleListingId: string;
    newVehicleListingId: string;
    dealId: string;
  }) {
    const vehicleListing = this.getById(newVehicleListingId);
    if (!vehicleListing) return;

    // remove the previous vehicle listing id --> deal id
    this.storeReferenceToDealByVehicleListingId.delete(
      previousVehicleListingId,
    );

    // set the deal id
    this.addToIndexedForeignKeys(dealId, vehicleListing);
  }

  addToIndexedForeignKeys(deal_id: string, vehicleListing: VehicleListing) {
    this.storeIndexedByForeignKey.set(deal_id, vehicleListing);
    this.storeReferenceToDealByVehicleListingId.set(vehicleListing.id, deal_id);
  }

  getById(id: string): VehicleListing | undefined {
    return this.store.get(id);
  }

  get all(): VehicleListing[] {
    return Array.from(this.store.values());
  }

  get availableVehicles(): VehicleListing[] {
    return this.all
      .filter((vehicle) => vehicle.purchase_status === 'AVAILABLE')
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  get purchaseInProgressVehicles(): VehicleListing[] {
    return this.all
      .filter((vehicle) => vehicle.purchase_status === 'PURCHASE_IN_PROGRESS')
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  get soldVehicles(): VehicleListing[] {
    return this.all
      .filter((vehicle) => vehicle.purchase_status === 'SOLD')
      .sort((a, b) => b.created_at.localeCompare(a.created_at));
  }

  get groupedByPurchaseStatus(): VEHICLE_LISTINGS_GROUPED_BY_PURCHASE_STATUS {
    const grouped = this.all.reduce((acc, vehicleListing) => {
      acc[vehicleListing.purchase_status as VehicleListingPurchaseStatus] = acc[
        vehicleListing.purchase_status as VehicleListingPurchaseStatus
      ] || {
        purchase_status: vehicleListing.purchase_status,
        count: 0,
        vehicles: [],
      };
      acc[vehicleListing.purchase_status as VehicleListingPurchaseStatus]
        .count++;
      acc[
        vehicleListing.purchase_status as VehicleListingPurchaseStatus
      ].vehicles.push(vehicleListing);
      return acc;
    }, {} as VEHICLE_LISTINGS_GROUPED_BY_PURCHASE_STATUS);

    Object.values(grouped).forEach((group) => {
      group.vehicles.sort((a, b) => b.created_at.localeCompare(a.created_at));
    });

    return grouped;
  }

  save(vehicleListing: VehicleListing) {
    this.store.set(vehicleListing.id, vehicleListing);
  }

  upsert(vehicleListing: VehicleListingType) {
    this.add(vehicleListing);
  }

  // actions
  updateStatus({
    id,
    status,
  }: {
    id: string;
    status: VehicleListing['status'];
  }) {
    const vehicleListing = this.getById(id);
    if (!vehicleListing) return;

    vehicleListing.status = status;
    this.save(vehicleListing);
  }

  updatePurchaseStatus({
    id,
    purchase_status,
  }: {
    id: string;
    purchase_status: VehicleListing['purchase_status'];
  }) {
    const vehicleListing = this.getById(id);
    if (!vehicleListing) return;

    vehicleListing.purchase_status = purchase_status;
    this.save(vehicleListing);
  }

  updateNumberOfKeys({
    id,
    numberOfKeys,
  }: {
    id: string;
    numberOfKeys: VehicleListing['num_of_keys'];
  }) {
    const vehicleListing = this.getById(id);
    if (!vehicleListing) return;

    vehicleListing.num_of_keys = numberOfKeys;
    this.save(vehicleListing);
  }

  updateVehicleHistory({
    id,
    vehicleHistory,
  }: {
    id: string;
    vehicleHistory: VehicleListing['vehicle_history'];
  }) {
    const vehicleListing = this.getById(id);
    if (!vehicleListing) return;

    vehicleListing.vehicle_history = vehicleHistory;
    this.save(vehicleListing);
  }

  updatePreviousOwners({
    id,
    previousOwners,
  }: {
    id: string;
    previousOwners: VehicleListing['previous_owners'];
  }) {
    const vehicleListing = this.getById(id);
    if (!vehicleListing) return;

    vehicleListing.previous_owners = previousOwners;
    this.save(vehicleListing);
  }

  updateTransactionType({
    id,
    transaction_type,
  }: {
    id: string;
    transaction_type: VehicleListing['transaction_type'];
  }) {
    const vehicleListing = this.getById(id);
    if (!vehicleListing) return;

    vehicleListing.transaction_type = transaction_type;
    this.save(vehicleListing);
  }

  updateIsListedWholesale({
    id,
    is_listed_wholesale,
  }: {
    id: string;
    is_listed_wholesale: VehicleListing['is_listed_wholesale'];
  }) {
    const vehicleListing = this.getById(id);
    if (!vehicleListing) return;

    vehicleListing.is_listed_wholesale = is_listed_wholesale;
    this.save(vehicleListing);
  }

  updateRemainingBatteryWarrantyMiles({
    id,
    remainingBatteryWarrantyMiles,
  }: {
    id: string;
    remainingBatteryWarrantyMiles: VehicleListing['remaining_battery_warranty_miles'];
  }) {
    const vehicleListing = this.getById(id);
    if (!vehicleListing) return;
    vehicleListing.remaining_battery_warranty_miles =
      remainingBatteryWarrantyMiles;
    this.save(vehicleListing);
  }

  updateRemainingBatteryWarrantyMonths({
    id,
    remainingBatteryWarrantyMonths,
  }: {
    id: string;
    remainingBatteryWarrantyMonths: VehicleListing['remaining_battery_warranty_months'];
  }) {
    const vehicleListing = this.getById(id);
    if (!vehicleListing) return;
    vehicleListing.remaining_battery_warranty_months =
      remainingBatteryWarrantyMonths;
    this.save(vehicleListing);
  }

  async hydrateLocal(): Promise<void> {
    await hydrateFromWorker({
      tableName: 'vehicle_listings',
      entity: 'vehicle_listings',
      modelStore: vehicleListingStore,
    });
  }

  applyDeltas({ deltas }: { deltas: DeltaPacketRecord<VehicleListingType>[] }) {
    for (const delta of deltas) {
      this.applyDelta({ delta });
    }
  }

  async hydrateDeal(
    vehicleListingId: VehicleListingType['vehicle_id'],
  ): Promise<void> {
    const listing = await db.vehicle_listings.get(vehicleListingId);
    if (listing) this.upsert(listing);
  }
}

export const vehicleListingStore = new VehicleListingStore();

class VehicleFromIndexerStore
  implements BaseStore<VehicleFromIndexer, VehicleFromIndexerType>
{
  store: Map<string, VehicleFromIndexer> = new Map();

  constructor() {
    makeAutoObservable(this);
  }

  applyDeltas({
    deltas,
  }: {
    deltas: DeltaPacketRecord<VehicleFromIndexerType>[];
  }) {
    for (const delta of deltas) {
      this.applyDelta({ delta });
    }
  }

  applyDelta({ delta }: { delta: DeltaPacketRecord<VehicleFromIndexerType> }) {
    const vehicleFromIndexer = this.getById(delta.record_id);
    if (!vehicleFromIndexer) return;

    vehicleFromIndexer.updated_at = delta.data.updated_at;
    vehicleFromIndexer.vin = delta.data.vin;
    vehicleFromIndexer.make = delta.data.make;
    vehicleFromIndexer.model = delta.data.model;
    vehicleFromIndexer.trim = delta.data.trim;
    vehicleFromIndexer.body = delta.data.body;
    vehicleFromIndexer.charging_plug = delta.data.charging_plug;
    vehicleFromIndexer.battery_capacity = delta.data.battery_capacity;
    vehicleFromIndexer.epa_range = delta.data.epa_range;
    vehicleFromIndexer.autopilot_features = delta.data.autopilot_features;
    vehicleFromIndexer.exterior_color = delta.data.exterior_color;
    vehicleFromIndexer.interior_color = delta.data.interior_color;
    vehicleFromIndexer.top_features = delta.data.top_features;
    vehicleFromIndexer.year = delta.data.year;
    vehicleFromIndexer.seating_capacity = delta.data.seating_capacity;

    this.save(vehicleFromIndexer);

    // hydrate indexed db
    db.vehicle_from_indexer.put(vehicleFromIndexer, vehicleFromIndexer.id);
  }

  add(input: VehicleFromIndexerType) {
    const vehicleFromIndexer = new VehicleFromIndexer(input);
    this.store.set(vehicleFromIndexer.id, vehicleFromIndexer);
  }

  getById(id: string): VehicleFromIndexer | undefined {
    return this.store.get(id);
  }

  get all(): VehicleFromIndexer[] {
    return Array.from(this.store.values());
  }

  upsert(vehicleFromIndexer: VehicleFromIndexerType) {
    this.add(vehicleFromIndexer);
  }

  save(vehicleFromIndexer: VehicleFromIndexer) {
    this.store.set(vehicleFromIndexer.id, vehicleFromIndexer);
  }

  updateBody({ id, body }: { id: string; body: VehicleFromIndexer['body'] }) {
    const vehicleFromIndexer = this.getById(id);
    if (!vehicleFromIndexer) return;

    vehicleFromIndexer.body = body;
    this.save(vehicleFromIndexer);
  }

  updateChargingPlug({
    id,
    charging_plug,
  }: {
    id: string;
    charging_plug: VehicleFromIndexer['charging_plug'];
  }) {
    const vehicleFromIndexer = this.getById(id);
    if (!vehicleFromIndexer) return;

    vehicleFromIndexer.charging_plug = charging_plug;
    this.save(vehicleFromIndexer);
  }

  updateBatteryCapacity({
    id,
    batteryCapacity,
  }: {
    id: string;
    batteryCapacity: VehicleFromIndexer['battery_capacity'];
  }) {
    const vehicleFromIndexer = this.getById(id);
    if (!vehicleFromIndexer) return;

    vehicleFromIndexer.battery_capacity = batteryCapacity;
    this.save(vehicleFromIndexer);
  }

  updateEPARange({
    id,
    epaRange,
  }: {
    id: string;
    epaRange: VehicleFromIndexer['epa_range'];
  }) {
    const vehicleFromIndexer = this.getById(id);
    if (!vehicleFromIndexer) return;

    vehicleFromIndexer.epa_range = epaRange;
    this.save(vehicleFromIndexer);
  }

  updateAutopilot({
    id,
    autopilot,
  }: {
    id: string;
    autopilot: VehicleFromIndexer['autopilot_features'];
  }) {
    const vehicleFromIndexer = this.getById(id);
    if (!vehicleFromIndexer) return;

    vehicleFromIndexer.autopilot_features = autopilot;
    this.save(vehicleFromIndexer);
  }

  updateTopFeatures({
    id,
    topFeatures,
  }: {
    id: string;
    topFeatures: VehicleFromIndexer['top_features'];
  }) {
    const vehicleFromIndexer = this.getById(id);
    if (!vehicleFromIndexer) return;

    vehicleFromIndexer.top_features = topFeatures;
    this.save(vehicleFromIndexer);
  }

  updateSeatingCapacity({
    id,
    seatingCapacity,
  }: {
    id: string;
    seatingCapacity: VehicleFromIndexer['seating_capacity'];
  }) {
    const vehicleFromIndexer = this.getById(id);
    if (!vehicleFromIndexer) return;

    vehicleFromIndexer.seating_capacity = seatingCapacity;
    this.save(vehicleFromIndexer);
  }

  async hydrateLocal(): Promise<void> {
    await hydrateFromWorker({
      tableName: 'vehicle_from_indexer',
      entity: 'vehicle_from_indexer',
      modelStore: vehicleFromIndexerStore,
    });
  }

  async hydrateDeal(
    vehicleId: VehicleListingType['vehicle_id'],
  ): Promise<void> {
    const vehicle = await db.vehicle_from_indexer.get(vehicleId);
    if (vehicle) this.upsert(vehicle);
  }
}

export const vehicleFromIndexerStore = new VehicleFromIndexerStore();

type VehicleInventoryHydrationResponse<T, U> = {
  listings: T[];
  vehicles: U[];
};

class VehiclesAndListingsFromIndexerHydrationDelegator {
  async hydrate({
    fullHydrateEndpoint,
    deltaSyncEndpoint,
  }: {
    fullHydrateEndpoint: string;
    deltaSyncEndpoint: string;
  }) {
    const checkpoint = await db.inventorySyncCheck.get('vehicle_inventory');

    const isIndexedDBEmpty =
      (await db.vehicle_listings.count()) === 0 &&
      (await db.vehicle_from_indexer.count()) === 0;

    if (checkpoint && !isIndexedDBEmpty) {
      await this._hydrateDeltaSync({
        endpoint: deltaSyncEndpoint,
        checkpoint: checkpoint.date_synced,
      });
      this._postHydrateAssociateToDeals();
      return;
    }

    const resp =
      await pdkAxios.get<
        VehicleInventoryHydrationResponse<
          VehicleListingType,
          VehicleFromIndexerType
        >
      >(fullHydrateEndpoint);

    const vehicleListings = resp.data.listings;
    const vehicleFromIndexers = resp.data.vehicles;

    await this._hydrateIndexedDB(vehicleListings, vehicleFromIndexers);

    vehicleListings.forEach((listing) => vehicleListingStore.upsert(listing));
    vehicleFromIndexers.forEach((vehicle) =>
      vehicleFromIndexerStore.upsert(vehicle),
    );

    this._postHydrateAssociateToDeals();
  }

  async _hydrateDeltaSync({
    endpoint,
    checkpoint,
  }: {
    endpoint: string;
    checkpoint: string;
  }) {
    const url = `${endpoint}?delta=${checkpoint}`;
    const resp =
      await pdkAxios.get<
        VehicleInventoryHydrationResponse<
          VehicleListingType,
          VehicleFromIndexerType
        >
      >(url);

    const vehicleListings = resp.data.listings;
    const vehicleFromIndexers = resp.data.vehicles;

    await this._hydrateIndexedDB(vehicleListings, vehicleFromIndexers);

    const listingsFromIndexedDB = await db.vehicle_listings.toArray();
    const vehiclesFromIndexedDB = await db.vehicle_from_indexer.toArray();
    listingsFromIndexedDB.forEach((listing) =>
      vehicleListingStore.upsert(listing),
    );
    vehiclesFromIndexedDB.forEach((vehicle) =>
      vehicleFromIndexerStore.upsert(vehicle),
    );

    return;
  }

  async _hydrateIndexedDB(
    listings: VehicleListingType[],
    vehicles: VehicleFromIndexerType[],
  ) {
    if (listings.length === 0 && vehicles.length === 0) {
      return;
    }

    const utcDateString = this._createUTCDateStringFromDayJS();

    await db.transaction(
      'rw',
      db.vehicle_listings,
      db.vehicle_from_indexer,
      db.inventorySyncCheck,
      async () => {
        for (const listing of listings) {
          const vl = {
            id: listing.id.toString(),
            vehicle_id: listing.vehicle_id.toString(),
            created_at: listing.created_at,
            updated_at: listing.updated_at,
            status: listing.status,
            purchase_status: listing.purchase_status,
            price: listing.price,
            mileage: listing.mileage,
            num_of_keys: listing.num_of_keys,
            vehicle_history: listing.vehicle_history,
            previous_owners: listing.previous_owners,
            is_pre_order: listing.is_pre_order,
            remaining_battery_warranty_miles:
              listing.remaining_battery_warranty_miles,
            remaining_battery_warranty_months:
              listing.remaining_battery_warranty_months,
            images: listing.images,
            transaction_type: listing.transaction_type,
            is_listed_wholesale: listing.is_listed_wholesale,
          };
          await db.vehicle_listings.put(vl);
        }

        for (const vehicle of vehicles) {
          const vfi = {
            id: vehicle.id.toString(),
            vin: vehicle.vin,
            make: vehicle.make,
            model: vehicle.model,
            trim: vehicle.trim,
            year: vehicle.year,
            created_at: vehicle.created_at,
            updated_at: vehicle.updated_at,
            body: vehicle.body,
            charging_plug: vehicle.charging_plug,
            battery_capacity: vehicle.battery_capacity,
            epa_range: vehicle.epa_range,
            autopilot_features: vehicle.autopilot_features,
            exterior_color: vehicle.exterior_color,
            interior_color: vehicle.interior_color,
            top_features: vehicle.top_features,
            seating_capacity: vehicle.seating_capacity,
          };
          await db.vehicle_from_indexer.put(vfi);
        }

        await db.inventorySyncCheck.put({
          collection: 'vehicle_inventory',
          date_synced: utcDateString,
        });
      },
    );
  }

  _createUTCDateStringFromDayJS(): string {
    return dayjs().utc().toISOString();
  }

  _postHydrateAssociateToDeals() {
    for (const deal of dealStore.allNonCancelled) {
      const vehicleListing = vehicleListingStore.getById(
        deal.vehicle_listing_id,
      );
      if (vehicleListing) {
        vehicleListingStore.addToIndexedForeignKeys(deal.id, vehicleListing);
      }
    }
  }
}

export const vehiclesAndListingsFromIndexerHydrationDelegator =
  new VehiclesAndListingsFromIndexerHydrationDelegator();
